import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';

import styles from './PunchupBoxOffice.module.css';

export interface CardPaymentResult {
	stripeTokenId: string;
	last4: string;
}

export interface CardPaymentBillingDetails {
	email: string;
	name?: string;
}

export interface CardPaymentFormHandle {
	collect: (billingDetails: CardPaymentBillingDetails) => Promise<CardPaymentResult>;
}

export interface CardPaymentFormProps {
	/** Reports the Payment Element's completeness so the parent can gate the submit button. */
	onCompleteChange?: (complete: boolean) => void;
}

// Card-only Payment Element. `payment_method_types: ['card']` on the Elements provider keeps Link,
// Apple/Google Pay and ACH out. We only opt out (`never`) of name + email — the operator already
// captured those above and we pass them into the ConfirmationToken below. Stripe requires that every
// field marked `never` be supplied at confirmation time, so we must NOT mark `address` as `never`
// (we have no address to pass) — leaving it `auto` lets the Element collect the postal/ZIP the card
// needs for AVS. We create a *ConfirmationToken* (not a PaymentMethod): the backend forwards
// `session_token_id` to Stripe as a ConfirmationToken (financial.go), so it must be a `ctoken_…`.
const paymentElementOptions: StripePaymentElementOptions = {
	layout: 'tabs',
	fields: { billingDetails: { name: 'never', email: 'never' } },
	wallets: { applePay: 'never', googlePay: 'never' },
};

export const CardPaymentForm = forwardRef<CardPaymentFormHandle, CardPaymentFormProps>(({ onCompleteChange }, ref) => {
	const stripe = useStripe();
	const elements = useElements();

	// A freshly-mounted Element is always empty, so reset the parent's completeness gate. This covers
	// the remount that happens when the card flow reappears (e.g. tickets re-added after the total hit $0).
	useEffect(() => {
		onCompleteChange?.(false);
	}, [onCompleteChange]);

	useImperativeHandle(
		ref,
		(): CardPaymentFormHandle => ({
			collect: async (billingDetails) => {
				if (!stripe || !elements) {
					throw new Error('Stripe.js is not loaded yet.');
				}

				const { error: submitError } = await elements.submit();
				if (submitError) {
					throw new Error(submitError.message || 'Card details are invalid.');
				}

				const { error, confirmationToken } = await stripe.createConfirmationToken({
					elements,
					params: {
						payment_method_data: {
							billing_details: {
								email: billingDetails.email,
								name: billingDetails.name || undefined,
							},
						},
					},
				});
				if (error || !confirmationToken) {
					throw new Error(error?.message || 'Card details are invalid.');
				}

				const last4 = confirmationToken.payment_method_preview?.card?.last4 ?? '';
				return { stripeTokenId: confirmationToken.id, last4 };
			},
		}),
		[stripe, elements]
	);

	return (
		<div className={styles.stripeFormWrap}>
			<PaymentElement options={paymentElementOptions} onChange={(event) => onCompleteChange?.(event.complete)} />
		</div>
	);
});

CardPaymentForm.displayName = 'CardPaymentForm';
